<template>
  <form @submit.prevent.stop="invite">
    <div class="title">
      {{ modalTitle }}
    </div>
    <div class="body">
      <div v-if="isSingle">
        <div>
          <div class="form-group half">
            <label for="first-name">First Name</label>
            <input
              id="first-name"
              v-model="firstName"
              type="text"
            >
          </div>
          <div class="dg form-group half">
            <label for="last-name">Last Name</label>
            <input
              id="last-name"
              v-model="lastName"
              type="text"
            >
          </div>
        </div>
        <div class="dg form-group">
          <label for="email">Email Address</label>
          <input
            id="email"
            v-model="email"
            type="email"
            required="true"
          >
        </div>
      </div>
      <div v-else>
        <div class="dg form-group">
          <label for="email-list">Type or paste email addresses separated by commas or on separate lines.</label>
          <textarea
            id="email-list"
            v-model="emailList"
            required="true"
          />
          <div class="form-hint">
            For best performance, we recommend not pasting more than 50 emails at a time.
          </div>
          <div
            v-if="invalidEmails.length"
            class="form-hint"
            style="color:red"
          >
            These don't look like email addresses — please fix or remove them:
            {{ invalidEmails.join(', ') }}
          </div>
        </div>
      </div>
      <div class="dg form-group">
        <label for="invitation-text">Invitation Message</label>
        <textarea
          id="invitation-text"
          v-model="invitationText"
          rows="4"
        >
          Hello Everyone,

          We created this Rally as an all-in-one solution for us to stay organized and connected. It's one easy place for us to send messages, keep a group calendar, organize special events, collect money, as well as share files, photos, links, tasks, and more. We also love that it's private, secure, and ad-free!

          Thanks for all you do!
        </textarea>
      </div>
      <div>
        <div class="dg form-group stacked-labels checkboxes">
          <label>
            <input
              v-model="saveAsDefaultInvitationText"
              type="checkbox"
            >
            <div class="square" />
            <b>Save as default invitation message</b>
          </label>
        </div>
        <div class="dg form-group inline-labels radios">
          <label>Their role in this Rally</label>
          <label>
            <input
              v-model="role"
              name="role"
              type="radio"
              value="Friend"
            >
            <div class="circle" />
            <b>Invite As Friend</b>
          </label>
          <label>
            <input
              v-model="role"
              name="role"
              type="radio"
              value="Admin"
            >
            <div class="circle" />
            <b>Invite As Admin</b>
          </label>
        </div>
      </div>
    </div>
    <div class="footer">
      <div
        v-if="failedEmails.length"
        class="form-hint"
        style="color:red; flex:1; text-align:left"
      >
        These invitations failed — please try them again:
        {{ failedEmails.join(', ') }}
      </div>
      <button
        class="dg secondary"
        :disabled="sending"
        @click="close"
      >
        Cancel
      </button>
      <button
        class="dg primary"
        type="submit"
        :disabled="sending"
      >
        {{ sending ? 'Inviting ' + sentCount + ' of ' + totalToSend + '…' : 'Invite' }}
      </button>
    </div>
  </form>
</template>

<script>
  import { mapActions, mapState } from "vuex"

  export default {
    props: {
      initialValues: {
        default: null,
        required: false,
        type: Object,
      },
      modalVariant: {
        required: true,
        type: String,
      },
      rallyId: {
        required: true,
        type: Number,
      },
    },
    data() {
      return {
        email: '',
        firstName: '',
        invitationText: '',
        lastName: '',
        role: 'Friend',
        saveAsDefaultInvitationText: false,
        sending: false,
        sentCount: 0,
        totalToSend: 0,
        invalidEmails: [],
        failedEmails: [],
      }
    },
    computed: {
      ...mapState("rallies", ["allRalliesByRallyId"]),
      isSingle() {
        return this.modalVariant === 'single'
      },
      modalTitle() {
        return this.isSingle ? 'Invite someone to this Rally' : 'Send group invitations'
      },
    },
    mounted() {
      this.setupForm()
    },
    methods: {
      ...mapActions("memberships", ["createMemberInvite"]),
      ...mapActions("rallies", ["updateRally"]),
      ...mapActions("user", ["setModalProps"]),
      close() {
        this.setModalProps({})
      },
      // Matches the shape rmems validates server-side (URI::MailTo);
      // client-side we additionally require a dotted domain.
      isValidEmail(e) {
        return /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(e)
      },
      async invite() {
        this.invalidEmails = []
        this.failedEmails = []

        let listEntries = []
        if (this.emailList && this.emailList.length > 0) {
          // Normalize a local copy — mutating the bound value collapsed the
          // user's textarea into one comma-string on every attempt.
          // Split on commas/newlines first, then trim each entry — edge
          // whitespace is forgiven, but interior spaces stay visible so a
          // glued space-separated paste gets flagged instead of mangled.
          const normalized = this.emailList.replace(/\n/g, ",").replace(/,+/g, ",")
          listEntries = normalized.split(",").map(e => e.trim()).filter(e => e.length > 0)
          // Previously entries without an @ were silently discarded and the
          // success toast fired anyway ("bob" vanished without a trace).
          // Now nothing sends until every entry parses.
          this.invalidEmails = listEntries.filter(e => !this.isValidEmail(e))
          if (this.invalidEmails.length > 0) { return }
        }

        if (this.email && this.email.length > 0 && !this.isValidEmail(this.email.trim())) {
          this.invalidEmails = [this.email.trim()]
          return
        }

        this.sending = true
        this.sentCount = 0
        this.totalToSend = listEntries.length + ((this.email && this.email.length > 0) ? 1 : 0)
        const failed = []
        try {
          // Batches of 5: keeps rmems' worker pool breathing on big pastes
          // and lets the counter show real progress instead of a 25s freeze.
          const BATCH = 5
          for (let i = 0; i < listEntries.length; i += BATCH) {
            const batch = listEntries.slice(i, i + BATCH)
            const results = await Promise.allSettled(batch.map(e => {
              const arr = e.split("@");
              return this.createMemberInvite({
                createdVia: 'RallyFriends',
                invitationText: this.invitationText,
                rallyId: this.rallyId,
                role: this.role,
                status: 'Invited',
                firstName: arr[0],
                lastName: arr[1],
                email: e,
                title: this.title
              });
            }))
            results.forEach((r, idx) => {
              if (r.status === 'rejected') { failed.push(batch[idx]) }
            })
            this.sentCount = Math.min(i + BATCH, listEntries.length)
          }

          if (this.email && this.email.length > 0) {
            try {
              await this.createMemberInvite({
                createdVia: 'RallyFriends',
                email: this.email.trim(),
                firstName: this.firstName,
                invitationText: this.invitationText,
                lastName: this.lastName,
                rallyId: this.rallyId,
                role: this.role,
                status: 'Invited',
              })
              this.sentCount += 1
            } catch (e) {
              failed.push(this.email.trim())
            }
          }

          if (this.saveAsDefaultInvitationText) {
            const rallyDup = this.allRalliesByRallyId[this.rallyId]
            rallyDup.rally.attributes["invitation-message"] = this.invitationText
            await this.updateRally({rally: rallyDup, invitation_message: this.invitationText})
          }
        } finally {
          this.sending = false
        }

        if (failed.length > 0) {
          this.failedEmails = failed
          const sent = this.totalToSend - failed.length
          this.$store.dispatch('user/flashWarning',
            sent + ' invitation(s) sent, ' + failed.length + ' failed.');
          // Modal stays open so the failed addresses stay visible.
        } else {
          this.$store.dispatch('user/flashSuccess', 'Invitation(s) are on their way!');
          this.close();
        }
      },
      setupForm() {
        const rally = this.allRalliesByRallyId[this.rallyId].rally
        this.invitationText = rally.attributes["invitation-message"]

        if ( this.initialValues ) {
          this.email = this.initialValues.email
          this.firstName = this.initialValues.firstName
          this.lastName = this.initialValues.lastName
        }
      },
    },
  }
</script>
